/**
 * Bütün uçların fetch istemcisi. Oturum httpOnly çerezle taşınır: JS belirtece hiç dokunmaz.
 * Durum değiştiren isteklerde tarayıcı Origin başlığını kendisi yazar (sunucu bunu denetler).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const NETWORK_MESSAGE = 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.';
const UNEXPECTED_MESSAGE = 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.';

/**
 * Yakalanan bir fırlatmayı ekranda gösterilecek metne çevirir. `Error` olmayan ya da mesajsız
 * fırlatmalar boş bir kırmızı kutu bırakmasın diye genel metne düşer; asıl değer konsola yazılır
 * ki sebep kaybolmasın.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message !== '') return err.message;
  console.error('DENT-OS: beklenmeyen hata', err);
  return UNEXPECTED_MESSAGE;
}

/** 401/403 sonrası gidilecek ekran; yolu kabuk bilir, veri katmanı yalnızca haber verir. */
export type AuthFailure = 'sessionExpired' | 'unauthorized';

let authFailureHandler: ((failure: AuthFailure) => void) | null = null;

/**
 * Oturum düşmesini ve yetki reddini dinleyen tek işleyiciyi kaydeder (kabuk bir kez bağlar).
 * Yönlendirme tek yerde toplansın diye her istek buradan geçer; dönen işlev kaydı siler.
 */
export function onAuthFailure(handler: (failure: AuthFailure) => void): () => void {
  authFailureHandler = handler;
  return () => {
    if (authFailureHandler === handler) authFailureHandler = null;
  };
}

function reportAuthFailure(status: number, code: string): void {
  if (!authFailureHandler) return;
  if (status === 401 && code === 'UNAUTHENTICATED') {
    authFailureHandler('sessionExpired');
    return;
  }
  if (status === 403 && (code === 'FORBIDDEN' || code === 'MODULE_DISABLED')) {
    authFailureHandler('unauthorized');
  }
}

/**
 * Bir hata "uygulama cevap vermiyor" mu? Bağlantı hatası ve zarfsız 5xx (`send` ikisini de
 * NETWORK_ERROR diye paketler) ve ön kapının MAINTENANCE cevabı (Caddy arka uca ulaşamadığı
 * 502/503/504'ü de bu zarfla döner). KENDİ koduyla dönen 5xx kesinti DEĞİLDİR: uygulama
 * ayaktadır ve ekrana anlamlı bir hata yazmıştır (ör. 503 PAYMENT_PROVIDER_UNAVAILABLE,
 * 502 AI_PROVIDER_ERROR).
 */
export function isOutageError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.code === 'MAINTENANCE') return true;
  return err.code === 'NETWORK_ERROR' && (err.status === 0 || err.status >= 500);
}

/** Her isteğin sonucu: sunucuya ulaşıldı mı, yoksa kesinti mi (bakım ekranının girdisi). */
export type TransportOutcome = 'reachable' | 'outage';

let transportObserver: ((outcome: TransportOutcome) => void) | null = null;

/**
 * İsteklerin ulaşılabilirlik sonucunu dinleyen tek gözlemciyi kaydeder (data/system-status.ts).
 * Sistem durumu bu modülü içe aktarır; bu modül onu aktarmaz — döngü yerine kayıt.
 */
export function observeTransport(observer: (outcome: TransportOutcome) => void): () => void {
  transportObserver = observer;
  return () => {
    if (transportObserver === observer) transportObserver = null;
  };
}

/**
 * Bir isteğin kimlik bağlamı. İSTEMCİ TEK: gövde, içerik türü kuralı, hata zarfı ve ağ hatası
 * metni her uç için aynı yerde yaşar; değişen yalnızca bu üç alan.
 */
interface Transport {
  /** Mutlak istek adresi. Oturumlu uçlar `/api/v1` önekini alır, oturumsuz uçlar almaz. */
  url: string;
  credentials: RequestCredentials;
  /** 401/403'te kabuğu yönlendir. Kabuğun olmadığı sayfalarda anlamsızdır. */
  reportAuth: boolean;
}

async function send<T>(transport: Transport, init: RequestInit): Promise<T> {
  // İçerik türünü YALNIZCA JSON gövdesi varken yolluyoruz. Fastify başlık varsa boş gövdeyi de
  // JSON ayrıştırıcısına sokar ve FST_ERR_CTP_EMPTY_JSON_BODY ile 400 döner; logout, logout-all ve
  // oturum silme gövdesiz gittiği için başlık koşulsuz yazılırsa bu üç uç her çağrıda patlar.
  // `FormData` dışarıda bırakılır: sınırı (boundary) yalnızca tarayıcı bilir, başlığı elle
  // yazarsak `multipart/form-data; boundary=…` hiç gitmez ve sunucunun multipart ayrıştırıcısı
  // (apps/api/src/app.ts) devreye girmez — belge yükleme ve içe aktarmanın üç ucu da kırılır.
  const hasBody = init.body !== undefined && init.body !== null && !(init.body instanceof FormData);
  let response: Response;
  try {
    response = await fetch(transport.url, {
      credentials: transport.credentials,
      ...init,
      headers: {
        ...(hasBody ? { 'content-type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    // İPTAL kesinti değildir: React Query anahtar değişince, sayfa değişince ve çıkışta sorguları
    // iptal eder (hasta araması, hafta ileri, sayfalama). Sayılsaydı iki hızlı iptal çalışan
    // uygulamayı bakım ekranıyla örterdi.
    if (!init.signal?.aborted) transportObserver?.('outage');
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_MESSAGE, '');
  }

  if (response.status === 204) {
    transportObserver?.('reachable');
    return undefined as T;
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const envelope = body as {
      error?: { code: string; message: string; requestId: string; details?: unknown };
    };
    const error = envelope?.error
      ? new ApiError(
          response.status,
          envelope.error.code,
          envelope.error.message,
          envelope.error.requestId,
          envelope.error.details,
        )
      : new ApiError(response.status, 'NETWORK_ERROR', NETWORK_MESSAGE, '');
    transportObserver?.(isOutageError(error) ? 'outage' : 'reachable');
    if (transport.reportAuth) reportAuthFailure(error.status, error.code);
    throw error;
  }
  transportObserver?.('reachable');
  return body as T;
}

/**
 * @param silentAuthFailure Yalnızca `me()` için: oturumsuz açılışta gelen 401 bir "oturum düştü"
 * olayı değildir, kullanıcı henüz girmemiştir — yönlendirme tetiklenmez.
 */
export function request<T>(
  path: string,
  init: RequestInit = {},
  silentAuthFailure = false,
): Promise<T> {
  return send<T>(
    { url: `/api/v1${path}`, credentials: 'same-origin', reportAuth: !silentAuthFailure },
    init,
  );
}

/**
 * OTURUMSUZ uçlar (hastanın ödeme bağlantısı, spec §11). İkinci bir HTTP istemcisi DEĞİLDİR —
 * yukarıdaki `send`in aynısını farklı bir kimlik bağlamıyla çağırır (H4) — ama üç noktada
 * `request`ten bilerek ayrılır:
 *
 * 1. **Yol `/api/v1` öneki almaz.** Hastanın eline geçen bağlantı sürüm numarası taşımaz;
 *    uç `app.ts`te öneksiz kaydedilir (`/api/pay/:token`).
 * 2. **Oturum çerezi GÖNDERİLMEZ** (`credentials: 'omit'`). Uç kimlik bakmıyor; aynı
 *    tarayıcıda açık bir personel oturumu varsa çerezin gitmesi hastanın ziyaretini o
 *    personelin oturumuna iliştirilebilir kılardı. Gerekmeyen kimlik gönderilmez.
 * 3. **401/403 kabuğa haber vermez.** Bu sayfa kabuğun dışında çizilir (`public-entry.tsx`);
 *    `onAuthFailure` işleyicisini kimse bağlamamıştır ve "oturumunuz doldu" ekranına
 *    yönlendirmek hastaya hiçbir şey anlatmazdı — hiç oturumu yoktu.
 */
export function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return send<T>({ url: path, credentials: 'omit', reportAuth: false }, init);
}

export const apiGet = <T>(path: string) => request<T>(path);

/**
 * Yazma döngüsü sigortası. 1 Ekim 2026'da bir ekran döngüsü aynı uca 4 sn'de 55 yazma gönderdi;
 * klinik tek IP'den çıktığı için sunucu 429 döndü ve fail2ban bütün kliniği bir saat engelledi.
 * Hiçbir insan aynı kayda 10 saniyede 30 kez yazmaz: bundan fazlası ağa hiç çıkmaz, kullanıcı
 * ne olduğunu okur. Ölçü uç başınadır (sorgu dizesi hariç — sürüm her yazmada değişir), yani
 * başka bir kaydın yazması etkilenmez; pencere kayınca sigorta kendiliğinden açılır. Toplu bir
 * iş aynı uca bu kadar sık yazacaksa tek bir toplu uç kullanmalıdır.
 */
const WRITE_GUARD_WINDOW_MS = 10_000;
const WRITE_GUARD_MAX = 30;
export const WRITE_LOOP_MESSAGE =
  'Aynı kayıt kısa sürede çok kez gönderildi; güvenlik için durduruldu. Birkaç saniye bekleyip tekrar deneyin.';
const recentWrites = new Map<string, number[]>();

function guardWrite(method: string, path: string): void {
  const key = `${method} ${path.split('?')[0]}`;
  const now = Date.now();
  const recent = (recentWrites.get(key) ?? []).filter((at) => now - at < WRITE_GUARD_WINDOW_MS);
  if (recent.length >= WRITE_GUARD_MAX) {
    recentWrites.set(key, recent);
    console.error('DENT-OS: yazma döngüsü durduruldu', key);
    throw new ApiError(0, 'WRITE_LOOP_GUARD', WRITE_LOOP_MESSAGE, '');
  }
  recent.push(now);
  recentWrites.set(key, recent);
  // Uzun oturumda uç sayısı birikmesin: penceresi geçmiş kayıtlar ara ara atılır.
  if (recentWrites.size > 500) {
    for (const [other, times] of recentWrites) {
      if (now - (times[times.length - 1] ?? 0) >= WRITE_GUARD_WINDOW_MS) recentWrites.delete(other);
    }
  }
}

/** Tüm yazmalar tek yerden geçer ve Idempotency-Key taşır (spec §4.5). */
export function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  payload?: unknown,
  options: { idempotencyKey?: string } = {},
): Promise<T> {
  try {
    guardWrite(method, path);
  } catch (err) {
    return Promise.reject(err);
  }
  return request<T>(path, {
    method,
    ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    ...(options.idempotencyKey ? { headers: { 'idempotency-key': options.idempotencyKey } } : {}),
  });
}

/**
 * multipart: tarayıcı sınırı kendisi yazsın diye content-type ELLE konmaz. Yöntem varsayılan
 * POST'tur; klinik logosu yerine yazılan tek kayıttır ve PUT ile gider (C71).
 */
export function apiUpload<T>(
  path: string,
  form: FormData,
  options: { idempotencyKey?: string; method?: 'POST' | 'PUT' } = {},
): Promise<T> {
  const method = options.method ?? 'POST';
  try {
    guardWrite(method, path);
  } catch (err) {
    return Promise.reject(err);
  }
  return request<T>(path, {
    method,
    body: form,
    ...(options.idempotencyKey ? { headers: { 'idempotency-key': options.idempotencyKey } } : {}),
  });
}

export const newIdempotencyKey = (): string => crypto.randomUUID();

/**
 * İyimser kilit çakışması (spec §4.5): kaydı açtığımızdan beri başka biri değiştirdi. Ünit,
 * kullanıcı ve klinik ayarları yazmaları `expectedVersion` taşır; sunucu 409 VERSION_CONFLICT
 * döner. Sunucunun genel metni yerine kullanıcıya ne olduğunu ve ne yapacağını söyleriz.
 */
export const VERSION_CONFLICT_MESSAGE =
  'Bu kayıt siz düzenlerken başka biri tarafından değiştirildi. Güncel hâli yüklendi; kontrol edip yeniden kaydedin.';

export function isVersionConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === 'VERSION_CONFLICT';
}

/**
 * Aynı Idempotency-Key ile yeniden denenen yazma ÖNCEKİ denemede zaten kaydedilmiş, yalnızca
 * yanıtı kaybolmuş (sunucu 409 IDEMPOTENCY_ALREADY_APPLIED). Hata değildir: iş yapılmıştır;
 * çağıran ekranı tazeler ve başarı gibi davranır.
 */
export function isAlreadyApplied(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 409 && err.code === 'IDEMPOTENCY_ALREADY_APPLIED'
  );
}
